import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MessageCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSite } from "@/context/SiteContext";
import { useSeo, graph, faqLd, breadcrumbLd } from "@/hooks/useSeo";
import { PageHero, QuoteCta, Loading } from "@/components/site/Blocks";

export const openChat = () => window.dispatchEvent(new Event("ib-open-chat"));

function FaqGroup({ title, items, offset }) {
  return (
    <section className="mb-12" data-testid={`faq-group-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <h2 className="mb-2 font-display text-2xl uppercase text-[color:var(--ib-charcoal)]">{title}</h2>
      <Accordion type="multiple" className="border-t border-black/10">
        {items.map((f, i) => (
          <AccordionItem key={f.id} value={f.id}>
            <AccordionTrigger className="text-left text-base font-semibold sm:text-lg" data-testid={`faq-q-${offset + i}`}>{f.question}</AccordionTrigger>
            <AccordionContent className="whitespace-pre-line text-base leading-relaxed text-black/70">{f.answer}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
}

export default function Faq() {
  const { site } = useSite();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const faqs = useMemo(() => site?.faqs || [], [site]);
  const cats = useMemo(() => [...new Set(faqs.map((f) => f.category || "General"))], [faqs]);
  useSeo("faq", { jsonLd: faqs.length ? graph(faqLd(faqs), breadcrumbLd([["FAQ", "/faq"]])) : null });
  if (!site) return <Loading />;
  const term = q.trim().toLowerCase();
  const shown = faqs.filter((f) => (cat === "all" || (f.category || "General") === cat) && (!term || `${f.question} ${f.answer}`.toLowerCase().includes(term)));
  let offset = 0;
  return (
    <main>
      <PageHero eyebrow="FAQ" title="Questions? We've got answers." text="Everything you might want to know about building, remodeling and working with Ironbark Construction." image="/media/hero-living.jpg" />
      <section className="bg-[color:var(--ib-off)] py-14">
        <div className="mx-auto grid max-w-[1400px] gap-10 px-5 lg:grid-cols-[260px_1fr] lg:px-8">
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-3.5 text-black/40" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions…" aria-label="Search FAQs" className="w-full rounded-md border border-black/15 bg-white py-3 pl-9 pr-3 text-sm outline-none focus:border-[color:var(--ib-orange)]" data-testid="faq-search" />
            </div>
            <nav className="mt-4 flex flex-wrap gap-2 lg:flex-col" aria-label="FAQ categories">
              {["all", ...cats].map((c) => (
                <button key={c} onClick={() => setCat(c)} className={`rounded-md px-4 py-2 text-left text-xs font-bold uppercase tracking-widest transition-colors ${cat === c ? "bg-[color:var(--ib-charcoal)] text-white" : "bg-white text-black/70 hover:text-[color:var(--ib-orange)]"}`} data-testid={`faq-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{c === "all" ? "All questions" : c}</button>
              ))}
            </nav>
          </aside>
          <div className="bg-white p-6 sm:p-10" data-testid="faq-list-page">
            {shown.length === 0 && <p className="text-black/60" data-testid="faq-no-results">No questions match your search. Try different words, or ask Jason's assistant below.</p>}
            {(cat === "all" ? cats : [cat]).map((c) => {
              const items = shown.filter((f) => (f.category || "General") === c);
              if (!items.length) return null;
              const el = <FaqGroup key={c} title={c} items={items} offset={offset} />;
              offset += items.length;
              return el;
            })}
            <div className="mt-4 flex flex-col items-start gap-4 rounded-md border border-black/10 bg-[color:var(--ib-off)] p-6 sm:flex-row sm:items-center sm:justify-between">
              <div><p className="font-display text-xl uppercase">Still have a question?</p><p className="text-sm text-black/60">Ask Jason's assistant, call {site.settings.company?.phone}, or request a free estimate.</p></div>
              <div className="flex flex-wrap gap-3">
                <button onClick={openChat} className="ib-btn ib-btn-outline" data-testid="faq-ask-jason-btn"><MessageCircle size={16} /> Ask Jason</button>
                <Link to="/contact" className="ib-btn ib-btn-primary" data-testid="faq-quote-btn">Get a quote</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <QuoteCta />
    </main>
  );
}
